import React, { useState } from 'react';
import emailjs from '@emailjs/browser';
import { useLanguage } from '../context/LanguageContext';

const ContactForm = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });
  const [errors, setErrors] = useState({});

  // EmailJS configuration
  const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID || 'service_7voaaid';
  const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || 'template_x4w3fnq';
  const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || 'QlrzMrM6k1RoFJ-PY';

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Xóa lỗi khi user bắt đầu nhập
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = t('contact.errName');
    }
    
    if (!formData.email.trim()) {
      newErrors.email = t('contact.errEmail');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('contact.errEmailInvalid');
    }
    
    if (!formData.message.trim()) {
      newErrors.message = t('contact.errMessage');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    setSubmitStatus({ type: '', message: '' });
    setErrors({});

    try {
      // Gửi email qua EmailJS
      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        company: formData.company || '未記入',
        message: formData.message,
      };

      console.log('Sending email with params:', {
        service_id: EMAILJS_SERVICE_ID,
        template_id: EMAILJS_TEMPLATE_ID,
        public_key: EMAILJS_PUBLIC_KEY,
        template_params: templateParams
      });

      const result = await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams,
        EMAILJS_PUBLIC_KEY
      );

      console.log('EmailJS result:', result);

      if (result.status === 200 || result.text === 'OK') {
        // Lưu submission vào localStorage để kiểm tra sau
        const submission = {
          id: Date.now(),
          timestamp: new Date().toISOString(),
          ...templateParams
        };
        
        // Lấy danh sách submissions hiện có
        const existingSubmissions = JSON.parse(localStorage.getItem('contactSubmissions') || '[]');
        existingSubmissions.push(submission);
        
        // Lưu lại (giữ tối đa 50 submissions gần nhất)
        const recentSubmissions = existingSubmissions.slice(-50);
        localStorage.setItem('contactSubmissions', JSON.stringify(recentSubmissions));
        
        // Log thông tin để kiểm tra trong Console
        console.log('✅ Form submitted successfully!');
        console.log('📧 Submission details:', submission);
        console.log('📋 All submissions:', recentSubmissions);
        console.log('💡 To view all submissions, run in Console:');
        console.log('   JSON.parse(localStorage.getItem("contactSubmissions"))');
        
        setSubmitStatus({ 
          type: 'success', 
          message: t('contact.successMsg') 
        });
        setFormData({ name: '', email: '', company: '', message: '' });
      } else {
        throw new Error('Unexpected response: ' + JSON.stringify(result));
      }
    } catch (error) {
      console.error('Email sending error:', error);
      let errorMessage = t('contact.failMsg');
      
      if (error.text) {
        errorMessage = `${t('contact.failReason')}${error.text}`;
      } else if (error.message) {
        errorMessage = `${t('contact.failReason')}${error.message}`;
      }
      
      setSubmitStatus({ 
        type: 'error', 
        message: errorMessage
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white p-8 rounded-lg fade-in">
      <h4 className="text-blue-700 text-2xl font-bold mb-6">{t('contact.formTitle')}</h4>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            placeholder={t('contact.name')}
            className={`w-full p-3 border rounded text-gray-800 text-sm ${
              errors.name ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.name && (
            <p className="mt-1 text-sm text-red-600 text-left">{errors.name}</p>
          )}
        </div>
        
        <div>
          <input
            type="text"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            placeholder={t('contact.email')}
            className={`w-full p-3 border rounded text-gray-800 text-sm ${
              errors.email ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.email && (
            <p className="mt-1 text-sm text-red-600 text-left">{errors.email}</p>
          )}
        </div>
        
        <div>
          <input
            type="text"
            name="company"
            value={formData.company}
            onChange={handleInputChange}
            placeholder={t('contact.company')}
            className="w-full p-3 border border-gray-300 rounded text-gray-800 text-sm"
          />
        </div>
        
        <div>
          <textarea
            name="message"
            value={formData.message}
            onChange={handleInputChange}
            placeholder={t('contact.message')}
            rows={4}
            className={`w-full p-3 border rounded text-gray-800 text-sm resize-vertical ${
              errors.message ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.message && (
            <p className="mt-1 text-sm text-red-600 text-left">{errors.message}</p>
          )}
        </div>
        <button
          type="submit"
          disabled={isSubmitting}
          className={`bg-blue-700 text-white px-8 py-3 rounded transition-colors text-sm ${
            isSubmitting 
              ? 'opacity-50 cursor-not-allowed' 
              : 'hover:bg-blue-800'
          }`}
        >
          {isSubmitting ? t('contact.submitting') : t('contact.submit')}
        </button>
        
        {submitStatus.message && (
          <div className={`mt-4 p-3 rounded text-sm text-left ${
            submitStatus.type === 'success' 
              ? 'bg-green-100 text-green-700 border border-green-300' 
              : 'bg-red-100 text-red-700 border border-red-300'
          }`}>
            {submitStatus.message}
          </div>
        )}
      </form>
    </div>
  );
};

export default ContactForm;